/**
 * API Configuration
 *
 * Dynamically constructs API URLs based on the browser's current location.
 * This enables the application to work in any deployment scenario:
 * - Local development (localhost)
 * - Remote access via IP address (192.168.x.x)
 * - Customer self-hosted deployments (customer-domain.com)
 * - Production SAAS deployment
 *
 * The backend API is assumed to be on the same hostname as the frontend,
 * but on port 8080.
 */

/**
 * PDF Form Builder Backend API URL
 * Priority:
 * 1. Environment variable REACT_APP_API_URL (set in .env files)
 * 2. Dynamic detection from browser location (customer self-hosted deployments)
 *
 * For CAVU SAAS: Set in .env.production
 * For customer self-hosted: Auto-detects from browser, or customer can override in their .env
 */
/**
 * IMPORTANT: every call to this API must send credentials.
 *
 *   fetch(getApiUrl('/api/pdf/...'), { credentials: 'include', ... })
 *   axios.post(getApiUrl('/api/pdf/...'), body, { withCredentials: true })
 *
 * An anonymous visitor's tenancy is the HttpOnly `wsid` cookie and nothing
 * else - there is no license key hash in localStorage to fall back on. When
 * REACT_APP_API_URL points at a different origin than the app is served from,
 * fetch's default of `same-origin` silently omits that cookie: capabilities
 * load, the UI unlocks correctly, and every upload and session save then
 * arrives with no tenant. It looks right and is wrong in the data.
 *
 * Licensing Server calls are the exception and must NOT be credentialed. The
 * guest cookie is host-only (no Domain attribute) so it would never reach
 * them, and asking for credentials there only invites a CORS failure.
 */
export const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  `${window.location.protocol}//${window.location.hostname}:8080`;

/**
 * CAVU Licensing Server URL
 * Priority:
 * 1. Environment variable REACT_APP_LICENSING_SERVER_URL (set in .env files)
 * 2. Dynamic detection from browser location (development fallback only)
 *
 * This server is ALWAYS controlled by CAVU Innovations.
 * Customers never host their own licensing server.
 * Production URL: https://licensing.cavuinnovations.com (or similar)
 */
export const LICENSING_SERVER_URL =
  process.env.REACT_APP_LICENSING_SERVER_URL ||
  `${window.location.protocol}//${window.location.hostname}:1150`;

/**
 * CAVU Licensing Server *customer-facing portal* URL.
 *
 * Used for browser redirects (e.g., the nav-bar Sign In button) to the human-facing
 * Customer Portal where users sign in, manage their license, view billing, etc.
 *
 * This is intentionally separate from LICENSING_SERVER_URL because the API and the
 * portal live on different hostnames in production:
 *   Production: https://licensing.cavuinnovations.com
 *   QA:         https://qa-licensing.cavuinnovations.com
 *
 * Falls back to LICENSING_SERVER_URL only in dev where they may share a host.
 */
export const LICENSING_PORTAL_URL =
  process.env.REACT_APP_LICENSING_PORTAL_URL ||
  LICENSING_SERVER_URL;

/**
 * Helper function to construct full API endpoint URLs
 * @param path - The API path (e.g., '/api/pdf/upload')
 * @returns Full URL to the API endpoint
 */
export function getApiUrl(path: string): string {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
}

/**
 * Helper function to construct full Licensing Server *API* URLs.
 * Use this for backend/API calls, NOT for browser redirects to the customer portal.
 * @param path - The licensing server API path (e.g., '/api/licenses')
 * @returns Full URL to the licensing server API endpoint
 */
export function getLicensingUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${LICENSING_SERVER_URL}${normalizedPath}`;
}

/**
 * Helper function to construct full Licensing Server *customer-portal* URLs.
 * Use this for browser redirects to the human-facing portal (sign-in, billing, etc.).
 * @param path - The portal path (e.g., '/customer/login')
 * @returns Full URL to the customer portal
 */
export function getLicensingPortalUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${LICENSING_PORTAL_URL}${normalizedPath}`;
}

/**
 * License polling interval (milliseconds)
 * How often the frontend checks for license/tier changes from the backend.
 * Default: 300000ms (5 minutes)
 *
 * Configurable via REACT_APP_LICENSE_POLL_INTERVAL_MS environment variable.
 * Passed through Docker build args for deployment customization.
 */
export const LICENSE_POLL_INTERVAL_MS =
  parseInt(process.env.REACT_APP_LICENSE_POLL_INTERVAL_MS || '300000', 10);

/**
 * Environment detection
 */
export const isDevelopment = process.env.NODE_ENV === 'development';
export const isProduction = process.env.NODE_ENV === 'production';

/**
 * Logging helper for debugging API calls
 */
export function logApiCall(method: string, url: string): void {
  if (isDevelopment) {
    console.log(`[API] ${method} ${url}`);
  }
}
